
import { DictionaryEntry, LanguageValue } from '@sdbn/common';
import { MasterDetailListEntry } from '@sdbn/common';
import { v4 as uuidv4 } from 'uuid';

export enum TransportOptionConstraintType {
  MIN_SELECTED_COUNT = 'MIN_SELECTED_COUNT',
  MAX_SELECTED_COUNT = 'MAX_SELECTED_COUNT'
}

export class TransportOptionConstraint implements MasterDetailListEntry {
  id: number;
  name: string;
  count: number;
  type = TransportOptionConstraintType.MIN_SELECTED_COUNT;
  transportOptionIds = new Array<number>();
  deleted = false;

  constructor(constraint: TransportOptionConstraint) {
    this.id = constraint.id;
    this.name = constraint.name;
    this.count = constraint.count;
    this.type = constraint.type;
    if (constraint.transportOptionIds != null) {
      this.transportOptionIds.push(...constraint.transportOptionIds);
    }
    this.deleted = constraint.deleted;
  }

  public get listTitle(): string {
    return this.name;
  }

  public get listTitleDictionaryKey(): string {
    return null;
  }

  public get listSubTitle(): string {
    return null;
  }

  public get groupIdentifier(): String {
    return null;
  }

  public get childs(): Array<MasterDetailListEntry> {
    return null;
  }
}

export class EditableTransportOptionConstraint extends TransportOptionConstraint {

  invalid: boolean;

  constructor(constraint: any) {
    super(constraint);
    this.invalid = constraint.invalid;
  }

}

export class TransportOptionCondition {

  id: string | undefined;
  value = new DictionaryEntry();

  constructor(condition?: Partial<TransportOptionCondition>) {
    if (condition) {
      this.id = condition.id;
      if (condition.value) {
        this.value = new DictionaryEntry(condition.value);
      }
    }
  }
}

export class TransportOption {

  id: number;
  parentId: number;
  name = new DictionaryEntry();
  hintDriver = new DictionaryEntry();
  allwaysShowHint: boolean;
  optional: boolean;
  permittedProcessingTime: number;
  rowIndex: number;
  deleted = false;
  columnIndex: number;
  externalKey: string;
  mark: string;
  onlyInternal = false;
  noProcessingTimeThreshold = false;
  minimumSelectedOptions: number;
  timeSlotPriority = 0;
  color: string;
  globalConnectNotificationsEnabled: boolean;
  amountOfPallettesMandatory: boolean;
  amountOfBoxesMandatory: boolean;
  amountInKilogrammMandatory: boolean;
  amountInLiterMandatory: boolean;
  amountOthersMandatory: boolean;
  amountMandatoryInTerminal: boolean;
  amountMandatoryInTime: boolean;

  bookingOffsetHours: number;
  bookingOffsetTime: string;
  bookingOffsetDays: number;
  bookingOffsetType: BookingOffsetType;
  bookingOffsetWeeks: number;
  bookingOffsetWeekday: Weekday;

  preventGapsOnMultipleSlots: boolean;
  readOnlyAtTerminal: boolean;

  resources = new Array<string>();

  limitSchedules = new Array<TransportOptionLimitSchedule>();
  parameter = new Array<TransportOptionParameter>();
  conditionAssignments = new Array<string>();

  constructor(option?: any) {
    if (option) {
      this.merge(option);
    }
  }

  public merge(option: Partial<TransportOption>) {
    this.id = option.id;
    this.parentId = option.parentId;
    this.name = new DictionaryEntry(option.name);
    this.hintDriver = new DictionaryEntry(option.hintDriver);
    this.optional = option.optional;
    this.permittedProcessingTime = option.permittedProcessingTime;
    this.rowIndex = option.rowIndex;
    this.deleted = option.deleted;
    this.columnIndex = option.columnIndex;
    this.externalKey = option.externalKey;
    this.mark = option.mark;
    this.onlyInternal = option.onlyInternal;
    this.noProcessingTimeThreshold = option.noProcessingTimeThreshold;
    this.minimumSelectedOptions = option.minimumSelectedOptions || 0;
    this.timeSlotPriority = option.timeSlotPriority || 0;
    this.color = option.color;
    this.globalConnectNotificationsEnabled = option.globalConnectNotificationsEnabled;
    this.amountOfPallettesMandatory = option.amountOfPallettesMandatory;
    this.amountOfBoxesMandatory = option.amountOfBoxesMandatory;
    this.amountInKilogrammMandatory = option.amountInKilogrammMandatory;
    this.amountInLiterMandatory = option.amountInLiterMandatory;
    this.amountOthersMandatory = option.amountOthersMandatory;
    this.amountMandatoryInTerminal = option.amountMandatoryInTerminal;
    this.amountMandatoryInTime = option.amountMandatoryInTime;

    // this.hasParameter = option.hasParameter;
    // this.parameterName = new DictionaryEntry(option.parameterName);
    this.preventGapsOnMultipleSlots = option.preventGapsOnMultipleSlots;
    this.bookingOffsetHours = option.bookingOffsetHours;
    this.bookingOffsetTime = option.bookingOffsetTime;
    this.bookingOffsetDays = option.bookingOffsetDays;
    this.bookingOffsetType = option.bookingOffsetType;
    this.bookingOffsetWeeks = option.bookingOffsetWeeks;
    this.bookingOffsetWeekday = option.bookingOffsetWeekday;
    this.allwaysShowHint = option.allwaysShowHint;
    this.readOnlyAtTerminal = option.readOnlyAtTerminal;

    this.resources = new Array<string>();
    if (option.resources != null) {
      for (const resource of option.resources) {
        this.resources.push(resource);
      }
    }

    this.limitSchedules = new Array<TransportOptionLimitSchedule>();
    if (option.limitSchedules != null) {
      for (const schedule of option.limitSchedules) {
        this.limitSchedules.push(new TransportOptionLimitSchedule(schedule));
      }
    }

    this.parameter = new Array<TransportOptionParameter>();
    if (option.parameter != null) {
      for (const parameter of option.parameter) {
        this.parameter.push(new TransportOptionParameter(parameter));
      }
    }

    this.conditionAssignments = new Array<string>();
    if (option.conditionAssignments) {
      for (const id of option.conditionAssignments) {
        this.conditionAssignments.push(id);
      }
    }

  }
}

export class TransportOptionParameter {
  id: string | undefined;
  name = new DictionaryEntry();
  type: string | undefined;
  sortOrder: string | undefined;
  minLength: number | undefined;
  showInTerminal: boolean | undefined;
  mandatoryInTerminal: boolean | undefined;
  showInTime: boolean | undefined;
  mandatoryInTime: boolean | undefined;
  values = new Array<TransortOptionParameterValue>();
  constructor(parameter?: Partial<TransportOptionParameter>) {
    if (parameter) {
      this.id = parameter.id;
      this.type = parameter.type;
      this.name = new DictionaryEntry(parameter.name);
      this.minLength = parameter.minLength;
      this.showInTerminal = parameter.showInTerminal;
      this.mandatoryInTerminal = parameter.mandatoryInTerminal;
      this.showInTime = parameter.showInTime;
      this.mandatoryInTime = parameter.mandatoryInTime;
      this.sortOrder = parameter.sortOrder;
      if (parameter.values != null) {
        for (const value of parameter.values) {
          this.values.push(new TransortOptionParameterValue(value));
        }
      }
    }
  }
}

export class TransportOptionLimitSchedule {

  id?: string;
  dateFrom?: string;
  dateUntil?: string;
  unit?: string;
  limitMonday: number = 0;
  limitTuesday: number = 0;
  limitWednesday: number = 0;
  limitThursday: number = 0;
  limitFriday: number = 0;
  limitSaturday: number = 0;
  limitSunday: number = 0;

  limitExceptionSchedules = new Array<TransportOptionLimitExceptionSchedule>();

  constructor(schedule?: Partial<TransportOptionLimitSchedule>) {
    if (schedule) {
      this.id = schedule.id;
      this.dateFrom = schedule.dateFrom;
      this.dateUntil = schedule.dateUntil;
      this.unit = schedule.unit;
      this.limitMonday = schedule.limitMonday || 0;
      this.limitTuesday = schedule.limitTuesday || 0;
      this.limitWednesday = schedule.limitWednesday || 0;
      this.limitThursday = schedule.limitThursday || 0;
      this.limitFriday = schedule.limitFriday || 0;
      this.limitSaturday = schedule.limitSaturday || 0;
      this.limitSunday = schedule.limitSunday || 0;
      if (schedule.limitExceptionSchedules != null) {
        for (const exceptionSchedule of schedule.limitExceptionSchedules) {
          this.limitExceptionSchedules.push(new TransportOptionLimitExceptionSchedule(exceptionSchedule));
        }
      }
    }
  }

}

export class TransportOptionLimitExceptionSchedule {

  id?: string;
  dateFrom?: string;
  dateUntil?: string;
  unit?: string;
  limitMonday: number = 0;
  limitTuesday: number = 0;
  limitWednesday: number = 0;
  limitThursday: number = 0;
  limitFriday: number = 0;
  limitSaturday: number = 0;
  limitSunday: number = 0;

  constructor(schedule?: Partial<TransportOptionLimitExceptionSchedule>) {
    if (schedule) {
      this.id = schedule.id;
      this.dateFrom = schedule.dateFrom;
      this.dateUntil = schedule.dateUntil;
      this.unit = schedule.unit;
      this.limitMonday = schedule.limitMonday || 0;
      this.limitTuesday = schedule.limitTuesday || 0;
      this.limitWednesday = schedule.limitWednesday || 0;
      this.limitThursday = schedule.limitThursday || 0;
      this.limitFriday = schedule.limitFriday || 0;
      this.limitSaturday = schedule.limitSaturday || 0;
      this.limitSunday = schedule.limitSunday || 0;
    }
  }

}

export class TransportOptionConfiguration {

  transportOptions = new Array<TransportOption>();

  constraints = new Array<TransportOptionConstraint>();

  conditions = new Array<TransportOptionCondition>();

  constructor(configuration?: Partial<TransportOptionConfiguration>) {
    if (configuration) {
      if (configuration.transportOptions) {
        for (const o of configuration.transportOptions) {
          this.transportOptions.push(new TransportOption(o));
        }
      }
      if (configuration.constraints) {
        for (const c of configuration.constraints) {
          this.constraints.push(new TransportOptionConstraint(c));
        }
      }
      if (configuration.conditions) {
        for (const condition of configuration.conditions) {
          this.conditions.push(condition);
        }
      }
    }
  }

}

export class TransortOptionParameterValue {
  id: number;
  value = new DictionaryEntry();
  deleted: boolean;

  constructor(value?: any) {
    this.merge(value);
  }

  public merge(value?: any) {
    if (value != null) {
      this.id = value.id;
      this.value = value.value == null ? new DictionaryEntry() : new DictionaryEntry(value.value);
      this.deleted = value.deleted;
    }
  }
}

export class OptionsColumn {
  columnIndex: number;
  options = new Array<StructuredTransportOption>();
}

/**
 * Modellerweiterung zur Abbildung der Struktur im Editor
 */
export class StructuredTransportOption extends TransportOption {
  uuid: string;
  children: StructuredTransportOption[] = [];
  constructor(option?: any) {
    super(option);
    if (option) {
      this.uuid = option.uuid || uuidv4();
      if (option.children != null) {
        for (const child of option.children) {
          this.children.push(new StructuredTransportOption(child));
        }
      }
    } else {
      this.uuid = uuidv4();
    }
  }
}

export enum BookingOffsetType {
  NONE = 'NONE', //
  HOURS = 'HOURS', //
  TIME_AND_WORKDAYS = 'TIME_AND_WORKDAYS', //
  WEEKDAY_AND_WEEK_OFFSET = 'WEEKDAY_AND_WEEK_OFFSET'
}

export enum Weekday {
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
  SUNDAY = 'SUNDAY'
}
